"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ChevronDown, ChevronRight } from "lucide-react";

interface Category {
  id: string;
  name: string;
  slug: string;
  level: number | null;
  product_count?: number;
  white_picture?: string | null;
  black_picture?: string | null;
}

interface CategoryWithChildren {
  category: Category;
  children: Category[];
}

interface MegaMenuProps {
  type: "location" | "achat" | "acte-administratif";
}

export function MegaMenu({ type }: MegaMenuProps) {
  const router = useRouter();
  const [categories, setCategories] = useState<CategoryWithChildren[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    async function fetchCategories() {
      try {
        const res = await fetch(`/api/categories?type=${type}`);
        const data = await res.json();
        setCategories(data.categories || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchCategories();
  }, [type]);

  const labels: Record<string, string> = {
    location: "Location",
    achat: "Achat",
    "acte-administratif": "Acte administratif",
  };
  const label = labels[type];
  const baseHref = `/categories/${type === "acte-administratif" ? "acte-administratif" : type}`;

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        setIsOpen(true);
        // Sélectionner la première catégorie par défaut
        if (categories.length > 0 && !activeCategory) {
          setActiveCategory(categories[0].category.id);
        }
      }}
      onMouseLeave={() => {
        setIsOpen(false);
        setActiveCategory(null);
      }}
    >
      {/* Trigger — click navigates, hover opens menu */}
      <button
        onClick={() => {
          router.push(baseHref);
          setIsOpen(false);
        }}
        className="flex items-center gap-1 text-white text-sm font-medium hover:text-primary transition-colors py-3 cursor-pointer"
      >
        {label}
        <ChevronDown
          className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Mega Menu Dropdown */}
      {isOpen && !isLoading && (
        <div className={`absolute top-full bg-white rounded-b-xl shadow-xl border min-w-[1000px] z-50 ${type === "location" ? "left-0" : "-left-10"}`}>
          <div className="flex">
            {/* Catégories principales - Colonne gauche */}
            <div className="w-96 bg-gray-50 border-r py-2 shrink-0">
              <nav className="max-h-[450px] overflow-y-auto">
                {categories.map(({ category }) => (
                  <div
                    key={category.id}
                    className={`group ${
                      activeCategory === category.id ? "bg-white" : ""
                    }`}
                    onMouseEnter={() => setActiveCategory(category.id)}
                  >
                    <Link
                      href={`/categories/${category.slug}?type=${type}`}
                      className={`flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                        activeCategory === category.id
                          ? "text-secondary font-medium bg-white"
                          : "text-gray-700 hover:text-secondary hover:bg-white"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {category.black_picture && (
                          <img
                            src={category.black_picture}
                            alt=""
                            className="w-5 h-5 object-contain"
                          />
                        )}
                        {category.name}
                      </span>
                      <ChevronRight className="h-4 w-4 opacity-50" />
                    </Link>
                  </div>
                ))}
              </nav>
            </div>

            {/* Sous-catégories - Zone principale */}
            <div className="flex-1 p-6">
              {activeCategory ? (
                <>
                  {categories
                    .filter(({ category }) => category.id === activeCategory)
                    .map(({ category, children }) => (
                      <div key={category.id}>
                        <Link
                          href={`/categories/${category.slug}?type=${type}`}
                          className="text-lg font-bold text-black mb-4 flex items-center gap-2 hover:text-secondary"
                        >
                          {category.black_picture && (
                            <img
                              src={category.black_picture}
                              alt=""
                              className="w-6 h-6 object-contain"
                            />
                          )}
                          {category.name}
                        </Link>
                        {children.length > 0 ? (
                          <div className="grid grid-cols-3 gap-x-8 gap-y-2">
                            {children.map((child) => (
                              <Link
                                key={child.id}
                                href={`/categories/${child.slug}?type=${type}`}
                                className="text-sm text-gray-600 hover:text-secondary py-1 transition-colors"
                              >
                                {child.name}
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">
                            Voir tous les produits de cette catégorie
                          </p>
                        )}
                      </div>
                    ))}
                </>
              ) : (
                <div className="text-center text-gray-400 py-12">
                  <p className="text-sm">
                    Survolez une catégorie pour voir les sous-catégories
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
